import React, { useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import { Box, InputAdornment, Paper, Stack, TextField, Typography } from '@mui/material_modern'
import ButtonList from 'components/generic/buttons/ButtonList'
import rem from 'utils/rem'
import { Search as SearchIcon } from '@mui/icons-material_modern'
import { debounce } from 'lodash'
import BasicDataGridV2 from 'components/generic/datagrid/BasicDataGridV2'
import { PAGE_SIZE } from 'utils/constants'

const DataGridWithSearchPaginated = ({
  textSearchPlaceholder,
  rows,
  columns,
  width,
  height,
  showActionBar,
  actionBarContent,
  showFilterChips,
  filterChips,
  renderViewModeToggle,
  actionButtons,
  allowedOptions,
  centerContent,
  setFilter,
  customPageSize,
  handleSearchChange,
  rowCount,
  fetchRows,
  ...restProps
}) => {
  // Create a debounced version of the handleSearchChange prop
  const debouncedHandleSearchChange = useCallback(debounce(handleSearchChange, 300), [])

  const handleInputChange = event => {
    const { value } = event.target
    debouncedHandleSearchChange(value)
  }
  // Clean up the debounced function on unmount
  useEffect(
    () => () => {
      debouncedHandleSearchChange.cancel()
    },
    [debouncedHandleSearchChange],
  )
  return (
    <Paper
      elevation={0}
      sx={{
        width,
        height,
        backgroundColor: 'background.transparent',
      }}
      data-testid="search-grid"
    >
      <Box
        mb={2}
        spacing={1}
        sx={{
          display: 'flex',
          justifyContent: centerContent ? 'left' : 'space-between',
          alignItems: 'center',
        }}
      >
        <Stack direction="row" flexWrap="wrap" spacing={1}>
          <TextField
            label="Search"
            id="outlined-start-adornment"
            placeholder={textSearchPlaceholder}
            sx={{ width: rem(350) }}
            onChange={handleInputChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            onClick={event => event.stopPropagation()}
            data-testid="search-grid-input"
          />
          {showFilterChips ? filterChips : null}
        </Stack>

        <Typography sx={{ margin: '16px' }}>{centerContent}</Typography>
        <ButtonList buttonsData={actionButtons} />
      </Box>
      {showActionBar ? actionBarContent : null}
      <BasicDataGridV2
        paginationMode="server" // Enable server-side pagination
        rowCount={rowCount} // Total number of rows
        onPageChange={fetchRows} // Load rows when pagination changes
        pageSize={customPageSize}
        pagination
        rows={rows}
        columns={columns}
        disableColumnMenu
        {...restProps}
      />
    </Paper>
  )
}

DataGridWithSearchPaginated.propTypes = {
  ...BasicDataGridV2.propTypes,
  textSearchPlaceholder: PropTypes.string,
  showActionBar: PropTypes.bool,
  actionBarContent: PropTypes.element,
  showFilterChips: PropTypes.bool,
  filterChips: PropTypes.element,
  renderViewModeToggle: PropTypes.func,
  renderDateRangePicker: PropTypes.func,
  actionButtons: PropTypes.array,
  customPageSize: PropTypes.number,
  allowedOptions: PropTypes.array,
  centerContent: PropTypes.string,
  handleSearchChange: PropTypes.func,
}

DataGridWithSearchPaginated.defaultProps = {
  ...BasicDataGridV2.defaultProps,
  textSearchPlaceholder: 'Search all fields',
  showActionBar: false,
  actionBarContent: null,
  showFilterChips: false,
  filterChips: null,
  renderViewModeToggle: () => null,
  renderDateRangePicker: () => null,
  handleSearchChange: () => null,
  actionButtons: [],
  customPageSize: PAGE_SIZE,
  allowedOptions: [],
  centerContent: '',
}

export default DataGridWithSearchPaginated
